import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { settingsApi } from './api';
import type { PageCopyNamespace, PageCopyUsages, PageCopyVia } from '@/lib/api/types';
import { keys } from '@/lib/query/keys';
import { Card } from '@/lib/ui/Card';
import { Button } from '@/lib/ui/Button';
import { Switch } from '@/lib/ui/Switch';
import { Tag } from '@/lib/ui/Tag';
import { ContentStatusPill } from '@/lib/ui';
import { MonoValue } from '@/lib/ui/Typography';
import { cn } from '@/lib/ui/cn';
import { useToast } from '@/lib/ui';
import { useApiError } from '@/lib/query/useApiError';
import { resolveI18n } from '@/lib/i18n/content';
import { useLocale } from '@/lib/i18n/useLocale';
import { formatRelative } from '@/lib/format';
import { PageCopyContentEditor, TreeProvider, type TreeOps } from './PageCopyFields';
import { coverage, isArComplete, mergePatch, setAtPath, type Json } from './pageCopyTree';

/**
 * Copy editor for one page-copy namespace. The document's *shape* is fixed — editors change
 * the text of existing fields, never add, remove, or restructure them — so this is a focused
 * text-editing surface, not a JSON tree builder. Edits are saved with the **PATCH merge** route:
 * we diff the working tree against the last-saved snapshot and send only what changed, which is
 * concurrency-safe and audited (docs management.md). A sticky action bar keeps Save reachable
 * no matter how long the namespace is.
 */
export function PageCopyEditor({
  namespace,
  initial,
  focusField = null,
  focusLocale = null,
  onSaved,
}: {
  namespace: string;
  initial: PageCopyNamespace;
  /** Deep-link target field path (from global search) to scroll to / highlight. */
  focusField?: string | null;
  /** Locale the deep-link matched in, so we land on the right-language copy of the field. */
  focusLocale?: 'en' | 'ar' | null;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const onError = useApiError();

  // Last-saved snapshot (drives the dirty diff) + the live working copy.
  const [saved, setSaved] = useState<{ content: Json; ar: boolean; updatedAt: string }>({
    content: initial.content as Json,
    ar: initial.ar_complete,
    updatedAt: initial.updated_at,
  });
  const [content, setContent] = useState<Json>(initial.content as Json);
  const [arComplete, setArComplete] = useState(initial.ar_complete);

  // Reverse index: which pages consume this namespace. Drives the "Appears on" panel so an
  // editor can see where the copy they're changing actually shows up (see BE-page-copy-usages).
  const usages = useQuery({
    queryKey: keys.settings.pageCopyUsages(namespace),
    queryFn: () => settingsApi.pageCopyUsages(namespace),
  });

  // The editor only ever replaces the text at a leaf — no structural mutations.
  const ops: TreeOps = useMemo(
    () => ({ update: (path, value) => setContent((c) => setAtPath(c, path, value)) }),
    [],
  );

  const contentChanged = mergePatch(saved.content, content) !== null;
  const arChanged = arComplete !== saved.ar;
  const dirty = contentChanged || arChanged;

  const cov = coverage(content);
  const pct = cov.total === 0 ? 100 : Math.round((cov.translated / cov.total) * 100);
  const autoComplete = isArComplete(content);

  const save = useMutation({
    mutationFn: () => {
      const patch = mergePatch(saved.content, content);
      return settingsApi.patchPageCopy(namespace, {
        ...(patch ? { content: patch } : {}),
        ...(arChanged ? { ar_complete: arComplete } : {}),
      });
    },
    onSuccess: (data) => {
      setSaved({ content: data.content as Json, ar: data.ar_complete, updatedAt: data.updated_at });
      setContent(data.content as Json);
      setArComplete(data.ar_complete);
      toast.success(t('settings.pageCopy.savedTitle'), t('settings.pageCopy.savedDetail'));
      onSaved();
    },
    onError: (e) => onError(e),
  });

  const reset = () => {
    setContent(saved.content);
    setArComplete(saved.ar);
  };

  return (
    <Card>
      <div className="flex flex-col gap-md">
        {/* Header */}
        <div className="flex flex-col gap-2xs">
          <MonoValue className="text-base text-ink-deep">{namespace}</MonoValue>
          <p className="text-small text-ink-mute">
            {t('settings.pageCopy.updatedAt', { time: formatRelative(saved.updatedAt) })}
          </p>
        </div>

        {/* Status panel: Arabic coverage + the "translation complete" flag, kept up top so the
            two controls that aren't part of the field list stay reachable without scrolling. */}
        <div className="flex flex-col gap-sm rounded-md border-[0.5px] border-hairline bg-canvas-sand p-md">
          <CoverageMeter pct={pct} translated={cov.translated} total={cov.total} />
          <Switch
            label={t('settings.pageCopy.arabicComplete')}
            hint={
              autoComplete
                ? t('settings.pageCopy.arabicCompleteHintReady')
                : t('settings.pageCopy.arabicCompleteHintPending', {
                    count: cov.total - cov.translated,
                  })
            }
            checked={arComplete}
            onCheckedChange={setArComplete}
          />
        </div>

        {/* Editable fields */}
        <TreeProvider ops={ops}>
          <PageCopyContentEditor
            content={content}
            focusField={focusField}
            focusLocale={focusLocale}
          />
        </TreeProvider>

        {/* Pages that show this copy (reverse index) */}
        <UsagesPanel query={usages} />

        {/* Sticky action bar — pinned to the viewport bottom so Save is always one click away,
            however long the namespace. Spans the full card width by cancelling its padding. */}
        <div className="sticky bottom-0 -mx-lg -mb-lg mt-2xs flex items-center justify-between gap-sm border-t-[0.5px] border-hairline bg-canvas-paper/95 px-lg py-sm backdrop-blur-sm">
          <span
            className={cn(
              'text-small',
              dirty ? 'text-signal-amber-ink' : 'text-ink-mute',
            )}
          >
            {dirty ? t('settings.pageCopy.unsaved') : t('settings.pageCopy.allSaved')}
          </span>
          <div className="flex gap-sm">
            <Button variant="ghost" disabled={!dirty || save.isPending} onClick={reset}>
              {t('common.reset')}
            </Button>
            <Button loading={save.isPending} disabled={!dirty} onClick={() => save.mutate()}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * "Appears on N pages" panel — the reverse index that tells an editor where the copy they're
 * changing actually renders. Each consumer deep-links to its page editor; `via` shows whether
 * it's pulled in as a shared namespace, the page's CTA, or both. An empty result is surfaced as
 * an explicit "unused" hint (orphan namespaces are otherwise invisible).
 */
function UsagesPanel({ query }: { query: UseQueryResult<PageCopyUsages> }) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  if (query.isLoading) {
    return <p className="text-small text-ink-mute">{t('settings.pageCopy.usagesLoading')}</p>;
  }
  // Non-fatal: the editor still works without the reverse index, so degrade quietly.
  if (query.isError || !query.data) return null;

  const { usages, count } = query.data;

  return (
    <section className="flex flex-col gap-xs">
      <div className="flex items-center justify-between gap-sm">
        <span className="eyebrow text-ink-mute">{t('settings.pageCopy.usagesHeading')}</span>
        {count === 0 ? (
          <Tag variant="neutral">{t('settings.pageCopy.unused')}</Tag>
        ) : (
          <Tag variant="neutral">{t('settings.pageCopy.usedByCount', { count })}</Tag>
        )}
      </div>

      {count === 0 ? (
        <p className="text-small text-ink-mute">{t('settings.pageCopy.unusedHint')}</p>
      ) : (
        <ul className="divide-y divide-hairline overflow-hidden rounded-md border-[0.5px] border-hairline">
          {usages.map((u) => (
            <li key={u.entity_id}>
              <Link
                to={`/pages/${u.entity_id}`}
                className="flex items-center justify-between gap-sm px-md py-sm text-small hover:bg-canvas-sand"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-ink-deep">{resolveI18n(u.title, locale)}</span>
                  <MonoValue className="truncate text-label-sm text-ink-mute">
                    {u.key} · {u.via.map((v) => viaLabel(v, t)).join(' · ')}
                  </MonoValue>
                </span>
                <ContentStatusPill status={u.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Localized label for how a page references a namespace. */
function viaLabel(via: PageCopyVia, t: (k: string) => string): string {
  return via === 'cta_namespace'
    ? t('settings.pageCopy.viaCta')
    : t('settings.pageCopy.viaNamespaces');
}

function CoverageMeter({
  pct,
  translated,
  total,
}: {
  pct: number;
  translated: number;
  total: number;
}) {
  const { t } = useTranslation();
  const done = total > 0 && translated === total;
  return (
    <div className="flex flex-col gap-2xs">
      <div className="flex items-center justify-between gap-sm">
        <span className="eyebrow text-ink-mute">{t('settings.pageCopy.arCoverage')}</span>
        {total === 0 ? (
          <Tag variant="neutral">{t('settings.pageCopy.noFields')}</Tag>
        ) : (
          <Tag variant={done ? 'teal' : 'amber'}>
            {t('settings.pageCopy.coverageCount', { translated, total })}
          </Tag>
        )}
      </div>
      <div className="h-[6px] overflow-hidden rounded-pill bg-canvas-deep-sand">
        <div
          className={cn(
            'h-full rounded-pill transition-[width]',
            done ? 'bg-signal-teal' : 'bg-signal-amber',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
